<template>
    <section id="PatientInfo__Main">
        <section class="header-row align-items-center fw-bold">
            <span class="header-cell fw-bold user-name font-size-xl me-2">
                <a href="javascript:void(0);" @click="handlePatientNameClick()">
                    {{ selectedPatientDetails.DisplayNameWithMi }}
                </a>
            </span>
            <span class="header-cell fw-bold mt-sm-1 user-mrn" v-tooltip="{ title: 'Medical Record Number (MRN)' }">
                <label class="mb-0 mr-1">MRN:</label>
                <span>{{ selectedPatientDetails.PatientIdNumber }}</span>
            </span>
        </section>
        <section class="header-row fw-bold font-size-xs">
            <span class="header-cell" v-tooltip="{ title: 'Sex' }">
                <span v-if="selectedPatientDetails.Gender">
                    {{ selectedPatientDetails.Gender }}
                </span>
                <span class="text-default" v-else>No Gender Selected</span>
            </span>
            <span class="header-cell">
                <i class="fas fa-circle header-divider"></i>
                <div>
                    <span
                        class="badge CodeStatus__Red"
                        tooltip="title: Do Not Resuscitate"
                        v-if="selectedPatientDetails.IsDNR"
                        >DNR</span
                    >
                    <span class="badge CodeStatus__Green" v-else> Full Code </span>
                </div>
            </span>
            <span class="header-cell" v-if="patientRiskDetails?.IsSelectedFallsRiskAssessment">
                <span class="text-warning-status-label bg-warning-status rounded risk-label">Fall Risk</span>
            </span>
            <span class="header-cell" v-if="patientRiskDetails?.IsSelectedHighRiskForHospitalization">
                <span class="text-warning-status-label bg-warning-status rounded risk-label">Hospitalization Risk</span>
            </span>
            <span class="header-cell" v-if="patientRiskDetails?.IsSelectedHighRiskForInfection">
                <span class="text-warning-status-label bg-warning-status rounded risk-label">Infection Risk</span>
            </span>
            <span class="header-cell" v-if="patientTags.length">
                <i class="fas fa-circle header-divider"></i>
                <span v-tooltip="{ title: `${patientTags}` }"> Tags </span>
            </span>
        </section>
        <section class="header-row fw-bold font-size-xs">
            <section class="header-cell" v-tooltip="{ title: `Patient's Address` }">
                <i class="fas fa-map-marker-alt me-1 text-app-secondary-hh"> </i>
                <a v-if="hasAddress" :href="patientAddressLink" target="_blank">
                    {{ address }}
                </a>
                <span v-else class="text-default">Address Not Available</span>
            </section>
            <section class="header-cell" v-tooltip="{ title: `Patient's DOB & Age` }">
                <i class="fas fa-circle header-divider"></i>
                <i class="fa fa-birthday-cake me -1 text-app-secondary-hh" aria-hidden="true"></i>
                <span v-if="dobString">{{ dobString }}</span>
                <span class="text-default" v-else>DOB not available</span>
            </section>
            <section class="header-cell" v-tooltip="{ title: `Patient's Phone Number` }">
                <i class="fas fa-circle header-divider"></i>
                <i class="fa fa-mobile text-app-secondary-hh me-1" aria-hidden="true"></i>
                <a
                    :href="`tel: ${selectedPatientDetails.PhoneHomeFormatted}`"
                    v-if="selectedPatientDetails.PhoneHomeFormatted"
                >
                    {{ selectedPatientDetails.PhoneHomeFormatted }}
                </a>
                <span v-else class="text-default">Phone Not Available</span>
            </section>
            <section class="header-cell" v-tooltip="{ title: `Patient's Phone Number` }">
                <i class="fas fa-circle header-divider"></i>
                <i :class="`fas fa-phone-square me-1 text-app-secondary-hh`"></i>
                <a
                    :href="`tel: ${selectedPatientDetails.PhoneMobileFormatted}`"
                    v-if="selectedPatientDetails.PhoneMobileFormatted"
                >
                    {{ selectedPatientDetails.PhoneMobileFormatted }}
                </a>
                <span v-else class="text-default">Phone Not Available</span>
            </section>
        </section>
        <section class="header-row font-size-xs">
            <div class="information-column d-flex flex-column me-4">
                <section class="information-cell">
                    <b class="title">Episode</b>
                    <div>
                        <EmptyTextHandler :text="patientDetails.CurrentEpisodeDate" />
                    </div>
                </section>
                <section class="information-cell">
                    <b class="title">Insurance Name/ID</b>
                    <div>
                        <EmptyTextHandler :text="selectedPatientDetails.PrimaryInsuranceName" />
                    </div>
                </section>
            </div>
            <div class="information-column d-flex flex-column me-4">
                <section class="information-cell">
                    <b class="title">Start of Care Date</b>
                    <div>
                        <EmptyTextHandler :text="selectedPatientDetails.StartOfCareDateFormatted" />
                    </div>
                </section>
                <section class="information-cell">
                    <b class="title">Primary Contact</b>
                    <div>
                        <EmptyTextHandler
                            :text="`${selectedPatientDetails.EmergencyContact?.FirstName ? selectedPatientDetails.EmergencyContact?.FirstName : ''} ${selectedPatientDetails.EmergencyContact?.LastName ? selectedPatientDetails.EmergencyContact?.LastName : ''}`"
                        />
                        <a
                            class="text-link ms-1"
                            :href="`tel:${selectedPatientDetails.EmergencyContact?.PrimaryPhoneFormatted})`"
                            v-if="
                                selectedPatientDetails.EmergencyContact &&
                                selectedPatientDetails.EmergencyContact?.PrimaryPhoneFormatted
                            "
                            >{{ selectedPatientDetails.EmergencyContact.PrimaryPhoneFormatted }}</a
                        >
                        <span v-else>Not Available</span>
                    </div>
                </section>
            </div>
            <div class="information-column d-flex flex-column me-4">
                <section class="information-cell">
                    <b class="title">Attending Physician</b>
                    <div>
                        <span v-if="selectedPatientDetails.Physician?.DisplayName">{{
                            selectedPatientDetails.Physician?.DisplayName
                        }}</span>
                        <span v-else>Not Available</span>
                    </div>
                </section>
                <section class="information-cell">
                    <b class="title">Secondary Contact</b>
                    <div v-if="selectedPatientDetails.EmergencyContacts.length">
                        <EmptyTextHandler
                            :text="`${selectedPatientDetails.EmergencyContacts[0].FirstName} ${selectedPatientDetails.EmergencyContacts[0].LastName}`"
                        />
                        <a
                            class="text-link ms-1"
                            :href="`tel:${selectedPatientDetails.EmergencyContacts[0].PrimaryPhoneFormatted})`"
                            v-if="
                                selectedPatientDetails.EmergencyContacts &&
                                selectedPatientDetails.EmergencyContacts[0].PrimaryPhoneFormatted
                            "
                            >{{ selectedPatientDetails.EmergencyContacts[0].PrimaryPhoneFormatted }}</a
                        >
                    </div>
                    <div v-else>
                        <span class="text-default">Not Available</span>
                    </div>
                </section>
            </div>
        </section>
    </section>
</template>

<style src="./HHPatientMain.scss"></style>
<script lang="ts" src="./HHPatientMain.ts"></script>
